/**
 * One Dave card per deal (AGS-949/950)
 *
 * The PRD and the card catalogue: "a card shows only the latest event; the
 * full history is in the deal's change log". Dave writes a row per event (the
 * deal's issue or reminder, each price change, a cancel, a registration), so
 * the rows of one deal collapse into a single card that leads with the
 * newest, and the older ones are listed under it as earlier updates.
 *
 * The open issue or reminder leads whenever the deal has one: Dave rewrites
 * it every run, so it is the deal's current state, but it keeps the
 * created_at of its first run and would otherwise sort below older events.
 *
 * Only the cards still open are listed; the full change log (expired events
 * included) needs its own endpoint (V2).
 */
import { getAgentKey, isDaveMonitorDecision } from "../../../constants";

/**
 * Which deal a Dave row is about. Registration rows carry no deal_uuid (it is
 * assigned after the upload), so a deal is its ASIN, type and start date: an
 * ASIN can't have two deals of one type starting the same day. null when the
 * row says neither, so it stays a card of its own.
 */
export const getDaveDealKey = (daveData) => {
  const decision = daveData?.decision || {};
  const line = decision.deal_line || {};
  const dealType = decision.deal_type || line.deal_type;
  const startDate =
    line.start_date ||
    decision.start_date ||
    (decision.start_utc ? decision.start_utc.slice(0, 10) : null);
  if (dealType && startDate) {
    return `${daveData.asin ?? ""}|${dealType}|${startDate}`;
  }
  return decision.deal_uuid
    ? `${daveData.asin ?? ""}|${decision.deal_uuid}`
    : null;
};

const newestFirst = (a, b) =>
  new Date(b.created_at || 0) - new Date(a.created_at || 0);

/**
 * Collapse each deal's Dave rows into its lead row, in the lead's place.
 * The lead gets `daveEarlier`: the deal's other rows, newest first. Rows of
 * other agents and Dave rows with no deal key are returned untouched.
 */
export const collapseDaveDealCards = (decisions) => {
  const byDeal = new Map();
  decisions.forEach((d) => {
    if (getAgentKey(d) !== "dave") return;
    const key = getDaveDealKey(d);
    if (!key) return;
    if (!byDeal.has(key)) byDeal.set(key, []);
    byDeal.get(key).push(d);
  });

  const leads = new Map();
  byDeal.forEach((rows) => {
    if (rows.length < 2) return;
    const sorted = [...rows].sort(newestFirst);
    const lead = sorted.find(isDaveMonitorDecision) || sorted[0];
    leads.set(
      lead,
      sorted.filter((d) => d !== lead)
    );
  });
  const collapsed = new Set([...leads.values()].flat());

  return decisions
    .filter((d) => !collapsed.has(d))
    .map((d) => (leads.has(d) ? { ...d, daveEarlier: leads.get(d) } : d));
};
